import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { FaHistory, FaShieldAlt, FaCogs, FaFileInvoice, FaSync, FaExpand, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import TimeFilter from "../components/TimeFilter";
import InfoTooltip from "../components/InfoTooltip";
import TenantTabs from "../components/TenantTabs";
import ExpandedChartModal from "../components/ExpandedChartModal";
import LoadingOverlay from "../components/LoadingOverlay";
import ScrollableTable from "../components/ScrollableTable";
import { fetchAuditLogs, exportAuditLogs } from "../services/auditService";
import { getAvailablePlans } from "../services/analyticsService";
import { useTimeFilter } from "../context/TimeFilterContext";
import { getNodeTitle } from "../utils/nodeMappings"; // <--- ADD THIS

/**
 * @file Audit.js
 * @description Page for viewing system audit logs, filtered by tenant category and time.
 */
export default function Audit() {
  const [activeTab, setActiveTab] = useState("estrel");
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [data, setData] = useState(null);
  const [expandedChart, setExpandedChart] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [plans, setPlans] = useState([]);

  const handleExpandChart = (title, options, series, type) => {
    setExpandedChart({ title, options, series, type });
  };

  // Time Filter State

  const { timeFilter, setTimeFilter, customRange, handleCustomRangeChange,
    planFilter, setPlanFilter,
    availablePlans, setAvailablePlans
  } = useTimeFilter();


  const [logSortConfig, setLogSortConfig] = useState({ key: 'time', direction: 'desc' });

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const handleExportLogs = async () => {
    try {
      const res = await exportAuditLogs(timeFilter, customRange);
      if (res && res.status === "success" && res.downloadable_url) {
        // Trigger download via the returned URL
        const a = document.createElement('a');
        a.href = res.downloadable_url;
        a.target = "_blank"; // Open in new tab ensures download starts
        a.download = `audit_logs_${timeFilter}.csv`; // Hint filename
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        alert("Export failed: No download URL returned.");
      }
    } catch (error) {
      console.error("Failed to export audit logs:", error);
      alert("Failed to export logs. Please try again.");
    }
  };

  const loadData = (force = false) => {
    if (timeFilter === 'custom' && (!customRange?.start || !customRange?.end)) {
      return;
    }
    if (timeFilter === 'custom' && new Date(customRange.start) > new Date(customRange.end)) return;
    setLoading(true);

    if (force) {
      getAvailablePlans(timeFilter, customRange, true).then(setPlans);
    }

    fetchAuditLogs(timeFilter, customRange, planFilter, force).then((res) => {
      // Transform API response to Component State structure
      // console.log("Audit Load Data - Full Response:", res);
      // console.log("Config Trend Raw:", res.config_changes_over_time);

      const processed = {
        kpis: {
          totalEvents: res.summary_kpis?.total_events || 0,
          configChanges: res.summary_kpis?.config_changes || 0,
          securityEvents: res.summary_kpis?.security_events || 0,
          billingChanges: res.summary_kpis?.billing_changes || 0
        },
        charts: {
          heatmap: {
            series: (res.events_per_hour_heatmap || []).map(day => ({
              name: day.day,
              data: day.hours.map((val, h) => ({
                x: `${h.toString().padStart(2, '0')}:00`,
                y: val
              }))
            }))
          },
          configTrend: {
            categories: (res.config_changes_over_time || []).map(i => i.date),
            data: (res.config_changes_over_time || []).map(i => i.count)
          }
        },
        logs: (res.audit_log_entries || []).map(l => ({
          time: l.timestamp,
          tenant: l.tenant,
          actor: l.actor,
          action: l.action,
          target: l.target,
          module: l.module,
          status: l.result,
          // Mapping for compatibility, though tab filtering is disabled
          tenantCategory: "estrel"
        }))
      };

      setData(processed);
      setLastUpdated(new Date());

      // Delay to allow charts to render
      setTimeout(() => {
        setLoading(false);
      }, 600);
      setCurrentPage(1);
    }).catch(err => {
      console.error("Failed to load audit logs", err);
      setLoading(false);
    });
  };

  // Fetch available plans from dashboard service to populate dropdown
  useEffect(() => {
    if (timeFilter === 'custom' && (!customRange?.start || !customRange?.end)) return;
    if (timeFilter === 'custom' && new Date(customRange.start) > new Date(customRange.end)) return;
    getAvailablePlans(timeFilter, customRange).then(setAvailablePlans);
  }, [timeFilter, customRange, setAvailablePlans]);

  useEffect(() => {
    loadData();
  }, [timeFilter, customRange, planFilter]);

  /**
   * Filter logs based on active tenant tab (Estrel/Education/Enterprise).
   * Note: Currently bypassing client-side filtering to rely on API data.
   */
  const filteredLogs = React.useMemo(() => {
    let logs = data?.logs || [];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      try {
        logs = logs.filter(log =>
          Object.values(log).some(val => String(val).toLowerCase().includes(q))
        );
      } catch (e) {
        console.error("Filter error", e);
      }
    }
    return logs;
  }, [data, searchQuery]);

  if (!data) {
    return (
      <div className="min-vh-100 position-relative bg-light">
        <LoadingOverlay loading={true} message="Loading Audit Logs..." />
      </div>
    );
  }

  const handleLogSort = (key) => {
    let direction = 'asc';
    if (logSortConfig.key === key && logSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setLogSortConfig({ key, direction });
  };

  const sortedLogs = [...filteredLogs].sort((a, b) => {
    if (!logSortConfig.key) return 0;

    let aVal = a[logSortConfig.key];
    let bVal = b[logSortConfig.key];

    aVal = (aVal || '').toString().toLowerCase();
    bVal = (bVal || '').toString().toLowerCase();

    if (aVal < bVal) return logSortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return logSortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination Calculation
  const indexOfLastLog = currentPage * itemsPerPage;
  const indexOfFirstLog = indexOfLastLog - itemsPerPage;
  const currentLogs = sortedLogs.slice(indexOfFirstLog, indexOfLastLog);
  const totalPages = Math.ceil(sortedLogs.length / itemsPerPage);

  const renderSortArrow = (key) => {
    if (logSortConfig.key !== key) return <span className="text-muted ms-1" style={{ fontSize: '0.7em' }}>↕</span>;
    return logSortConfig.direction === 'asc' ? <span className="ms-1">↑</span> : <span className="ms-1">↓</span>;
  };

  const heatmapOptions = {
    chart: { type: "heatmap", toolbar: { show: false } },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.5,
        radius: 0,
        useFillColorAsStroke: true,
        colorScale: {
          ranges: [
            { from: 0, to: 0, color: '#cbd5e1', name: 'None' },
            { from: 1, to: 5, color: '#F4D677', name: 'Low' },
            { from: 6, to: 20, color: '#E68F17', name: 'Medium' },
            { from: 21, to: 100, color: '#AD2D45', name: 'High' }
          ]
        }
      }
    },
    dataLabels: { enabled: false },
    stroke: { width: 1 },
    xaxis: { type: 'category' }
  };

  const lineOptions = {
    chart: { type: "line", toolbar: { show: false } },
    stroke: { curve: "smooth", width: 2 },
    colors: ["#AD2D45"],
    xaxis: { categories: data?.charts?.configTrend?.categories || [] },
    markers: { size: 4, hover: { size: 6 } }, // Ensure points are visible
  };

  return (
    <div className="p-4 bg-light min-vh-100 position-relative">
      <LoadingOverlay loading={loading} />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1">Audit & Logs</h4>
          <p className="text-muted small mb-0">View comprehensive audit trails of system actions and user activities.</p>
        </div>
        <div className="d-flex align-items-center gap-3">
          <select
            className="form-select form-select-sm shadow-sm border-secondary-subtle"
            style={{ width: "130px", fontWeight: "500" }}
            value={planFilter || ""}
            onChange={(e) =>
              setPlanFilter(e.target.value === "" ? null : e.target.value)
            }
          >
            <option value="">All Plans</option>
            {availablePlans.map((p) => (
              <option key={p} value={p}>
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </option>
            ))}
          </select>
          <TimeFilter
            value={timeFilter}
            onChange={setTimeFilter}
            startDate={customRange.start}
            endDate={customRange.end}
            onCustomRangeChange={handleCustomRangeChange}
          />
          <div className="text-end d-none d-md-block ms-2">
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>Last Update</div>
            <div className="fw-bold small">{lastUpdated ? lastUpdated.toLocaleTimeString() : "-"}</div>
          </div>
          <button className="btn btn-white border shadow-sm btn-sm px-3" onClick={() => loadData(true)} disabled={loading}>
            <FaSync className={loading ? "fa-spin me-2" : "me-2"} /> Refresh
          </button>
        </div>
      </div>

      {/* KPI CARDS */}
      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="card border-0 shadow-sm p-3 h-100">
            <div className="text-muted small fw-bold">
              Total Events (24h) <InfoTooltip text="Events logged in the last 24 hours." />
            </div>
            <h3 className="fw-bold mb-0">{data?.kpis?.totalEvents || 0}</h3>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card border-0 shadow-sm p-3 h-100">
            <div className="text-muted small fw-bold">
              Config Changes <InfoTooltip text="Configuration changes in the last 24 hours." />
            </div>
            <h3 className="fw-bold mb-0">{data?.kpis?.configChanges || 0}</h3>

          </div>
        </div>
        {/* <div className="col-xl-3 col-md-6">
          <div className="card border-0 shadow-sm p-3 h-100">
            <div className="text-muted small fw-bold">
              Security Events <InfoTooltip text="Login failures and permission changes." />
            </div>
            <h3 className="fw-bold mb-0">{data?.kpis?.securityEvents || 0}</h3>

          </div>
        </div>
        <div className="col-xl-3 col-md-6">
          <div className="card border-0 shadow-sm p-3 h-100">
            <div className="text-muted small fw-bold">
              Billing Changes <InfoTooltip text="Subscription and payment modifictions." />
            </div>
            <h3 className="fw-bold mb-0">{data?.kpis?.billingChanges || 0}</h3>
          </div>
        </div> */}
      </div>

      {/* CHARTS */}
      <div className="row g-4 mb-4">
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0">Events Heatmap <InfoTooltip text="Intensity of system events over time." /></h6>
              <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Events Heatmap", heatmapOptions, data?.charts?.heatmap?.series || [], "heatmap")}>
                <FaExpand />
              </button>
            </div>
            {data?.charts?.heatmap?.series?.length > 0 && data.charts.heatmap.series.some(s => s.data.some(d => d.y > 0)) ? (
              <Chart options={heatmapOptions} series={data?.charts?.heatmap?.series || []} type="heatmap" height={250} />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted small bg-light rounded" style={{ minHeight: "250px" }}>
                No events recorded
              </div>
            )}
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm p-4 h-100">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0">Config Changes Over Time <InfoTooltip text="Frequency of system configuration updates." /></h6>
              <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Config Changes Over Time", lineOptions, [{ name: "Changes", data: data?.charts?.configTrend?.data || [] }], "line")}>
                <FaExpand />
              </button>
            </div>
            {data?.charts?.configTrend?.data?.some(v => v > 0) ? (
              <Chart options={lineOptions} series={[{ name: "Changes", data: data?.charts?.configTrend?.data || [] }]} type="line" height={250} />
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted small bg-light rounded" style={{ minHeight: "250px" }}>
                No configuration changes
              </div>
            )}
          </div>
        </div>
      </div>

      {/* TENANT TABS */}
      <TenantTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {/* LOGS TABLE (Refactored to match TenantManagement) */}
      <div className="card border-0 shadow-sm rounded-3 mt-4">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <h6 className="fw-bold mb-0">Audit Log ({activeTab})</h6>
            <div className="d-flex align-items-center gap-1 bg-light rounded px-2 py-1">
              <button className="btn btn-sm btn-link text-dark p-0 text-decoration-none" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} style={{ fontSize: '1.2rem', lineHeight: '1' }}>
                <FaChevronLeft size={12} />
              </button>
              <span className="small text-muted mx-2" style={{ fontSize: '0.8rem', minWidth: '70px', textAlign: 'center' }}>
                {currentPage} / {totalPages || 1}
              </span>
              <button className="btn btn-sm btn-link text-dark p-0 text-decoration-none" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage >= totalPages} style={{ fontSize: '1.2rem', lineHeight: '1' }}>
                <FaChevronRight size={12} />
              </button>
            </div>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={`Search ${activeTab} logs...`}
              style={{ maxWidth: '200px' }}
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            />
            <button className="btn btn-sm btn-outline-secondary" onClick={handleExportLogs}>Export CSV</button>
          </div>
        </div>
        <div className="card-body p-0">
          <ScrollableTable
            title=""
            data={currentLogs}
            columns={[
              { header: "Timestamp", accessor: "time", render: (val) => <span className="text-muted small font-monospace">{val}</span> },
              { header: "Subscriber", accessor: "tenant", render: (val) => <span>{val}</span> },
              { header: "Actor", accessor: "actor" },
              { header: "Action", accessor: "action", render: (val) => <span>{val}</span> },
              { header: "Target", accessor: "target", render: (val) => <span className="text-muted">{val}</span> },
              { header: "Module", accessor: "module", render: (val) => <span className="badge bg-light text-dark border">{getNodeTitle(val)}</span> },
              { header: "Result", accessor: "status", render: (val) => <span className={`badge ${val === "Success" ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`}>{val}</span> }
            ]}
            searchPlaceholder={`Search ${activeTab} logs...`}
            height={500}
            sortConfig={logSortConfig}
            onSort={handleLogSort}
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
            hideSearch={true}
          />
        </div>
      </div>
      <ExpandedChartModal
        show={!!expandedChart}
        onClose={() => setExpandedChart(null)}
        {...expandedChart}
      />
    </div>
  );
}
